import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Save,
  X,
  Loader2,
  Settings,
  AlertCircle,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import masterService from "../../services/masterService";
import { Button } from "../../components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils/utils";
import { useAuth } from "../../context/AuthContext";

const MasterList = () => {
  const { type } = useParams();
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const typeLabels = {
    industry: "Industry",
    "revenue-goal": "Revenue Goal",
    capability: "Capability",
    "business-problem": "Business Problem",
    "lead-status": "Lead Status",
    "lead-stage": "Lead Stage",
    "sales-stage": "Sales Stage",
  };

  const currentLabel = typeLabels[type] || "Master";
  const permissionModules = {
    industry: "masters.industry",
    "revenue-goal": "masters.revenue_goal",
    capability: "masters.capability",
    "business-problem": "masters.business_problem",
    "lead-status": "masters.lead_status",
    "lead-stage": "masters.lead_stage",
    "sales-stage": "masters.sales_stage",
  };
  const permissionModule = permissionModules[type];
  const canCreate = hasPermission(`${permissionModule}.create`);
  const canUpdate = hasPermission(`${permissionModule}.update`);
  const canDelete = hasPermission(`${permissionModule}.delete`);
  const canReorder = hasPermission(`${permissionModule}.reorder`);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await masterService.getMasters(type);
      setItems(data);
    } catch (error) {
      toast.error(`Failed to fetch ${currentLabel}`);
    } finally {
      setLoading(false);
    }
  }, [type, currentLabel]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    try {
      await masterService.createMaster(type, { name: newItemName });
      toast.success(`${currentLabel} added successfully`);
      setNewItemName("");
      setIsAdding(false);
      fetchItems();
    } catch (error) {
      toast.error(`Failed to add ${currentLabel}`);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;

    try {
      const item = items.find((i) => i.id === id);
      await masterService.updateMaster(type, id, {
        name: editName,
        sort_order: item.sort_order,
      });
      toast.success(`${currentLabel} updated successfully`);
      setEditingId(null);
      fetchItems();
    } catch (error) {
      toast.error(`Failed to update ${currentLabel}`);
    }
  };

  const handleDelete = async () => {
    if (itemToDelete === null || itemToDelete === undefined) return;

    try {
      setIsDeleting(true);
      await masterService.deleteMaster(type, itemToDelete);
      toast.success(`${currentLabel} deleted successfully`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (error) {
      toast.error(`Failed to delete ${currentLabel}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    // Optimistically update UI
    const updatedWithOrders = newItems.map((item, index) => ({
      ...item,
      sort_order: index + 1,
    }));
    setItems(updatedWithOrders);

    try {
      await masterService.updateOrder(
        type,
        updatedWithOrders.map((item) => ({
          id: item.id,
          sort_order: item.sort_order,
        })),
      );
      toast.success("Order updated");
    } catch (error) {
      toast.error("Failed to update order");
      fetchItems(); // Revert on failure
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {currentLabel} Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage and reorder your {currentLabel.toLowerCase()} categories.
          </p>
        </div>
        {canCreate && <Button
          onClick={() => setIsAdding(true)}
          className="bg-primary hover:bg-primary/90 text-secondary gap-2"
        >
          <Plus size={18} />
          Add New
        </Button>}
      </div>

      {isAdding && (
        <form
          onSubmit={handleAdd}
          className="mb-6 animate-in slide-in-from-top duration-200"
        >
          <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm flex items-center gap-3">
            <input
              autoFocus
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder={`Enter new ${currentLabel.toLowerCase()} name...`}
              className="flex-1 px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
            />
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-secondary"
            >
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsAdding(false)}
              className="text-slate-500"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="masters">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-3"
            >
              {items.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={String(item.id)}
                  index={index}
                  isDragDisabled={!canReorder}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn(
                        "bg-white border p-4 rounded-xl flex items-center gap-4 transition-all",
                        snapshot.isDragging
                          ? "shadow-lg ring-2 ring-primary border-transparent"
                          : "border-slate-200 hover:border-primary/20 hover:shadow-md",
                      )}
                    >
                      {canReorder && <div
                        {...provided.dragHandleProps}
                        className="text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600"
                      >
                        <GripVertical size={20} />
                      </div>}

                      <div className="flex-1">
                        {editingId === item.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              autoFocus
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="flex-1 px-3 py-1 rounded-md border border-primary outline-none"
                            />
                            <button
                              onClick={() => handleUpdate(item.id)}
                              className="text-green-600 hover:bg-green-50 p-1.5 rounded-md transition-colors"
                            >
                              <Save size={18} />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="text-slate-400 hover:bg-slate-50 p-1.5 rounded-md transition-colors"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ) : (
                          <span className="font-medium text-slate-800">
                            {item.name}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        {canUpdate && <button
                          onClick={() => {
                            setEditingId(item.id);
                            setEditName(item.name);
                          }}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        >
                          <Pencil size={18} />
                        </button>}
                        {canDelete && <button
                          onClick={() => {
                            setItemToDelete(item.id);
                            setDeleteDialogOpen(true);
                          }}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 size={18} />
                        </button>}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {items.length === 0 && !loading && !isAdding && (
        <div className="text-center py-20 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
            <Settings size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-700">
            No {currentLabel} found
          </h3>
          <p className="text-slate-500 mb-6">
            Start by adding your first {currentLabel.toLowerCase()}.
          </p>
          {canCreate && <Button
            onClick={() => setIsAdding(true)}
            className="bg-primary hover:bg-primary/90 text-secondary"
          >
            Add New {currentLabel}
          </Button>}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="text-red-500" size={24} />
            </div>
            <DialogTitle className="text-center text-xl font-bold text-slate-900">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-center text-slate-500 mt-2">
              Are you sure you want to delete this {currentLabel.toLowerCase()}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 rounded-2xl border-slate-200"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 rounded-2xl bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterList;
